import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InstagramIcon from '@mui/icons-material/Instagram';
import SendIcon from '@mui/icons-material/Send';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { addLiveChatSocketListener, connectLiveChatSocket } from 'src/utils/liveChatSocket';
import { useToaster } from 'src/utils/toaster/toasterContext';

const INSTAGRAM_MESSAGE_PAGE_SIZE = 10;

const getInitialInstagramID = () => new URLSearchParams(window.location.search).get('instagram_id') || new URLSearchParams(window.location.search).get('account_id') || '';

const getMessagesData = (conversation) => conversation?.messages?.data || conversation?.messages || [];

const getMessageText = (message) => message?.text || message?.message || '';

const getMessageImageURL = (message) => message?.image_url || message?.thumb_url || '';

const getMessageVideoURL = (message) => message?.video_url || '';

const getMessageAttachmentURL = (message) => message?.attachment_url || '';

const getAttachmentName = (message) => {
  if (message?.attachment_name) return message.attachment_name;
  const url = getMessageAttachmentURL(message);
  try {
    const parsed = new URL(url);
    const fileName = parsed.pathname.split('/').filter(Boolean).pop();
    return fileName || 'Attachment';
  } catch (_error) {
    return 'Attachment';
  }
};

const isSharedAttachment = (message) => ['share', 'link', 'fallback'].includes(String(message?.attachment_type || '').toLowerCase());

const getAttachmentDisplayURL = (message) => {
  const url = getMessageAttachmentURL(message);
  try {
    const parsed = new URL(url);
    return `${parsed.hostname}${parsed.pathname}`;
  } catch (_error) {
    return url;
  }
};

const getAttachmentLinkLabel = (message) => {
  if (isSharedAttachment(message)) return 'Open shared post/reel';
  return getAttachmentName(message);
};

const getSocketCreatedTime = (timestamp) => {
  const value = Number(timestamp || 0);
  return value > 0 ? new Date(value).toISOString() : new Date().toISOString();
};

const mergeMessages = (currentMessages, nextMessages) => {
  const seen = new Set();
  return [...currentMessages, ...nextMessages].filter((message) => {
    const id = message?.id || '';
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const getConversationParticipant = (conversation, instagramID) => {
  const participants = conversation?.participants?.data || conversation?.participants || [];
  return participants.find((participant) => participant.id !== instagramID) || participants[0] || null;
};

export default function InstagramChatView() {
  const { showToast } = useToaster();
  const [accounts, setAccounts] = useState([]);
  const [selectedInstagramID, setSelectedInstagramID] = useState(getInitialInstagramID());
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagePagination, setMessagePagination] = useState('');
  const [reply, setReply] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesBodyRef = useRef(null);
  const showToastRef = useRef(showToast);
  const selectedConversationRef = useRef(null);

  const selectedAccount = useMemo(
    () => accounts.find((account) => String(account.id) === String(selectedInstagramID) || account.profile_id === selectedInstagramID) || null,
    [accounts, selectedInstagramID]
  );

  const selectedProviderInstagramID = selectedAccount?.profile_id || '';

  const selectedParticipant = useMemo(
    () => getConversationParticipant(selectedConversation, selectedProviderInstagramID),
    [selectedConversation, selectedProviderInstagramID]
  );

  const visibleMessages = useMemo(() => [...messages].reverse(), [messages]);

  const scrollToLatestMessage = useCallback(() => {
    requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ block: 'end' }));
  }, []);

  const markAccountRead = useCallback((instagramID = selectedInstagramID) => {
    if (!instagramID) return;
    setAccounts((currentAccounts) =>
      currentAccounts.map((account) =>
        account.profile_id === instagramID ? { ...account, unread_count: 0 } : account
      )
    );
    window.dispatchEvent(new CustomEvent('brand2chat:social-unread-refresh', { detail: { service: 'instagram' } }));
  }, [selectedInstagramID]);

  const fetchAccounts = useCallback((silent = false) => {
    if (!silent) setLoadingAccounts(true);
    HttpServices.Get({
      url: `${userModuleURL.connectedProfiles}?service_name=instagram`,
      successCallback: (data) => {
        const nextAccounts = (data?.accounts || []).filter((account) => account.is_active !== false && account.is_enable !== false);
        setAccounts(nextAccounts);
        setSelectedInstagramID((current) => {
          const selected = nextAccounts.find(
            (account) => String(account.id) === String(current) || String(account.profile_id) === String(current)
          );
          return String(selected?.profile_id || nextAccounts[0]?.profile_id || '');
        });
        if (!silent) setLoadingAccounts(false);
      },
      failureCallback: (message) => {
        if (!silent) setLoadingAccounts(false);
        showToastRef.current(message || 'Failed to fetch Instagram accounts', 'e');
      },
    });
  }, []);

  const fetchConversations = useCallback((instagramID = selectedInstagramID, silent = false) => {
    if (!instagramID) {
      setConversations([]);
      setSelectedConversation(null);
      setMessages([]);
      return;
    }
    if (!silent) setLoadingConversations(true);
    const params = new URLSearchParams({ instagram_id: instagramID });
    HttpServices.Get({
      url: `${userModuleURL.instagramConversations}?${params.toString()}`,
      successCallback: (data) => {
        const nextConversations = data?.conversations || [];
        setConversations(nextConversations);
        setSelectedConversation((current) => {
          const matchingConversation = current && nextConversations.find((conversation) => conversation.id === current.id);
          return matchingConversation || nextConversations[0] || null;
        });
        if (!silent) setLoadingConversations(false);
        markAccountRead(instagramID);
      },
      failureCallback: (message) => {
        if (!silent) setLoadingConversations(false);
        showToastRef.current(message || 'Failed to fetch Instagram conversations', 'e');
      },
    });
  }, [markAccountRead, selectedInstagramID]);

  const fetchMessages = useCallback((conversationID, instagramID = selectedInstagramID, silent = false, after = '', append = false) => {
    if (!conversationID || !instagramID) {
      setMessages([]);
      setMessagePagination('');
      return;
    }
    if (append) setLoadingMoreMessages(true);
    else if (!silent) setLoadingMessages(true);
    const params = new URLSearchParams({ instagram_id: instagramID, limit: String(INSTAGRAM_MESSAGE_PAGE_SIZE) });
    if (after) params.set('after', after);
    HttpServices.Get({
      url: `${userModuleURL.instagramConversationMessages(conversationID)}?${params.toString()}`,
      successCallback: (data) => {
        const nextMessages = data?.messages || [];
        setMessages((currentMessages) => (append ? mergeMessages(currentMessages, nextMessages) : nextMessages));
        setMessagePagination(data?.pagination || '');
        if (append) setLoadingMoreMessages(false);
        else if (!silent) setLoadingMessages(false);
        if (!append) scrollToLatestMessage();
      },
      failureCallback: (message) => {
        if (append) setLoadingMoreMessages(false);
        else if (!silent) setLoadingMessages(false);
        showToastRef.current(message || 'Failed to fetch Instagram messages', 'e');
      },
    });
  }, [scrollToLatestMessage, selectedInstagramID]);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchConversations(selectedInstagramID);
  }, [fetchConversations, selectedInstagramID]);

  useEffect(() => {
    fetchMessages(selectedConversation?.id, selectedInstagramID);
  }, [fetchMessages, selectedConversation?.id, selectedInstagramID]);

  useEffect(() => {
    connectLiveChatSocket();
    const removeListener = addLiveChatSocketListener((data) => {
      if (data.type !== 'instagram:message:new') {
        return;
      }
      const payload = data.payload || {};
      const instagramID = payload.instagram_id || '';
      fetchAccounts(true);
      if (instagramID !== selectedAccount?.profile_id) {
        return;
      }
      const currentConversation = selectedConversationRef.current;
      const participant = getConversationParticipant(currentConversation, instagramID);
      if (currentConversation?.id && payload.sender_id && participant?.id === payload.sender_id) {
        const attachmentURL = String(payload.attachment_url || '').trim();
        const attachmentType = String(payload.attachment_type || '').toLowerCase();
        const liveMessage = {
          id: payload.message_id || `live:${payload.sender_id}:${payload.timestamp || Date.now()}`,
          text: payload.text || '',
          created_time: getSocketCreatedTime(payload.timestamp),
          from: { id: payload.sender_id, name: participant?.name || 'Instagram customer' },
          to: { id: instagramID, name: selectedAccount?.display_name || 'Instagram account' },
          attachment_url: attachmentURL,
          attachment_type: attachmentType,
          image_url: attachmentType === 'image' ? attachmentURL : '',
          video_url: attachmentType === 'video' ? attachmentURL : '',
        };
        setMessages((currentMessages) => (currentMessages.some((message) => message.id === liveMessage.id) ? currentMessages : [liveMessage, ...currentMessages]));
        scrollToLatestMessage();
      }
      fetchConversations(instagramID, true);
      if (currentConversation?.id) {
        window.setTimeout(() => fetchMessages(currentConversation.id, instagramID, true), payload.attachment_url ? 1200 : 0);
      }
    });
    return removeListener;
  }, [fetchAccounts, fetchConversations, fetchMessages, scrollToLatestMessage, selectedAccount?.display_name, selectedAccount?.profile_id]);

  const handleMessagesScroll = useCallback((event) => {
    if (
      event.currentTarget.scrollTop <= 24 &&
      messagePagination &&
      !loadingMessages &&
      !loadingMoreMessages &&
      selectedConversation?.id
    ) {
      fetchMessages(selectedConversation.id, selectedInstagramID, true, messagePagination, true);
    }
  }, [fetchMessages, loadingMessages, loadingMoreMessages, messagePagination, selectedConversation?.id, selectedInstagramID]);

  const sendReply = () => {
    const body = reply.trim();
    const recipientID = selectedParticipant?.id || '';
    const instagramID = selectedProviderInstagramID || selectedInstagramID;
    if (!body || !selectedAccount?.id || !instagramID || !recipientID || sending) return;
    setSending(true);
    HttpServices.Post({
      url: userModuleURL.instagramMessages,
      body: {
        account_id: selectedAccount.id,
        instagram_id: instagramID,
        conversation_id: selectedConversation?.id || '',
        recipient_id: recipientID,
        message: body,
      },
      successCallback: () => {
        setReply('');
        setSending(false);
        showToast('Instagram message sent', 's');
        fetchMessages(selectedConversation?.id, selectedInstagramID);
        fetchConversations(selectedInstagramID, true);
      },
      failureCallback: (message) => {
        setSending(false);
        showToast(message || 'Instagram message could not be sent', 'e');
      },
    });
  };

  const formatTime = (value) => (value ? CommonHelper.formatDateAndTime(value) : '');

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, height: 'calc(100vh - 96px)', minHeight: 620 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Instagram Chat
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <InstagramIcon sx={{ color: '#E1306C' }} />
          <Select
            size="small"
            value={selectedInstagramID}
            displayEmpty
            onChange={(event) => {
              setSelectedInstagramID(event.target.value);
              setSelectedConversation(null);
              setMessages([]);
              setMessagePagination('');
            }}
            sx={{ minWidth: 260, bgcolor: 'background.paper' }}
          >
            <MenuItem value="" disabled>
              Select Instagram account
            </MenuItem>
            {accounts.map((account) => (
              <MenuItem key={account.id} value={String(account.profile_id)}>
                {account.display_name || 'Instagram account'}
                {Number(account.unread_count || 0) > 0 ? ` (${account.unread_count} new)` : ''}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ height: 'calc(100% - 72px)', minHeight: 0 }}>
        <Grid item xs={12} md={4} lg={3} sx={{ height: '100%', minHeight: 0 }}>
          <Paper variant="outlined" sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
              <Typography fontWeight={800}>Conversations</Typography>
              {selectedAccount && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {selectedAccount.display_name || selectedAccount.profile_id}
                  </Typography>
                  {Number(selectedAccount.unread_count || 0) > 0 && (
                    <Chip size="small" color="error" label={`${selectedAccount.unread_count} new`} />
                  )}
                </Stack>
              )}
            </Box>
            <Divider />
            {(loadingAccounts || loadingConversations) && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            )}
            <List sx={{ overflowY: 'auto', minHeight: 0, flex: 1, p: 0 }}>
              {conversations.map((conversation) => {
                const participant = getConversationParticipant(conversation, selectedProviderInstagramID);
                const latestMessage = getMessagesData(conversation)[0] || {};
                const preview = getMessageText(latestMessage) || (getMessageImageURL(latestMessage) || getMessageVideoURL(latestMessage) || getMessageAttachmentURL(latestMessage) ? 'Attachment' : 'No message preview');
                return (
                  <ListItemButton
                    key={conversation.id}
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    sx={{ alignItems: 'flex-start', py: 1.5 }}
                  >
                    <Avatar sx={{ mr: 1.5, bgcolor: '#fce7f3', color: '#E1306C' }}>
                      <InstagramIcon fontSize="small" />
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography fontWeight={800} noWrap>
                          {participant?.name || 'Instagram customer'}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="caption" color="text.secondary" display="block" noWrap>
                            {participant?.id || conversation.id}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.primary" display="block" noWrap>
                            {preview}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })}
              {!loadingConversations && conversations.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography fontWeight={800}>No Instagram chats</Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9} sx={{ height: '100%', minHeight: 0 }}>
          <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedConversation ? (
              <>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f8fafc' }}>
                  <Avatar sx={{ bgcolor: '#fce7f3', color: '#E1306C' }}>
                    <InstagramIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" fontWeight={900} noWrap>
                      {selectedParticipant?.name || 'Instagram customer'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {selectedAccount?.display_name || selectedInstagramID}
                    </Typography>
                  </Box>
                </Box>
                <Divider />
                <Box ref={messagesBodyRef} onScroll={handleMessagesScroll} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc' }}>
                  {loadingMoreMessages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={18} />
                    </Box>
                  )}
                  {loadingMessages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  )}
                  {visibleMessages.map((message) => {
                    const isAgent = message?.from?.id === selectedProviderInstagramID;
                    const messageText = getMessageText(message);
                    const imageURL = getMessageImageURL(message);
                    const videoURL = getMessageVideoURL(message);
                    const attachmentURL = getMessageAttachmentURL(message);
                    const showAttachmentLink = attachmentURL && (!imageURL || isSharedAttachment(message)) && (!videoURL || isSharedAttachment(message));
                    return (
                      <Box key={message.id} sx={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start', mb: 1.5 }}>
                        <Card
                          variant="outlined"
                          sx={{
                            maxWidth: { xs: '88%', md: '68%' },
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: isAgent ? '#fce7f3' : '#fff',
                            borderColor: isAgent ? '#f9a8d4' : 'divider',
                          }}
                        >
                          {messageText && (
                            <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {messageText}
                            </Typography>
                          )}
                          {imageURL && (
                            <Box
                              component="img"
                              src={imageURL}
                              alt="Instagram attachment"
                              sx={{ display: 'block', maxWidth: 280, width: '100%', borderRadius: 1, mt: messageText ? 1 : 0 }}
                            />
                          )}
                          {videoURL && (
                            <Box
                              component="video"
                              src={videoURL}
                              controls
                              sx={{ display: 'block', maxWidth: 280, width: '100%', borderRadius: 1, mt: messageText || imageURL ? 1 : 0 }}
                            />
                          )}
                          {showAttachmentLink && (
                            <Box
                              component="a"
                              href={attachmentURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'block',
                                mt: messageText ? 1 : 0,
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'primary.main',
                                textDecoration: 'none',
                                wordBreak: 'break-word',
                              }}
                            >
                              <Typography component="span" fontWeight={800} color="primary.main">
                                {getAttachmentLinkLabel(message)}
                              </Typography>
                              <Typography component="span" variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {getAttachmentDisplayURL(message)}
                              </Typography>
                            </Box>
                          )}
                          {!messageText && !imageURL && !videoURL && !attachmentURL && (
                            <Typography color="text.secondary">Attachment</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                            {formatTime(message.created_time)}
                          </Typography>
                        </Card>
                      </Box>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>
                <Divider />
                <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={reply}
                      placeholder="Reply on Instagram..."
                      onChange={(event) => setReply(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    <IconButton color="primary" onClick={sendReply} disabled={sending || !reply.trim() || !selectedParticipant?.id}>
                      {sending ? <CircularProgress size={22} /> : <SendIcon />}
                    </IconButton>
                  </Stack>
                </Box>
              </>
            ) : (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center', p: 3 }}>
                <Box>
                  <InstagramIcon sx={{ fontSize: 54, color: '#E1306C', mb: 1 }} />
                  <Typography variant="h5" fontWeight={900}>
                    Select an Instagram chat
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}




